import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Button,
  Link,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { authApi } from "../../services/api/auth.api";
import { handleApiError } from "../../utils/error-handler";

type VerifyStatus = "verifying" | "success" | "error";

const wrapperSx = {
  p: 2,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "calc(100vh - 64px)",
} as const;

const cardSx = {
  maxWidth: 480,
  width: "100%",
  p: 4,
  boxShadow: (theme: Theme) => theme.shadows[3],
} as const;

/**
 * Страница подтверждения email по ссылке из письма.
 *
 * Флоу: письмо ведёт на `${FRONTEND_URL}/auth/verify-email?token=…` → эта страница
 * читает токен из query и дергает BE `GET /auth/verify/:token`. При успехе — кнопка
 * «Войти» (без авто-логина: BE не возвращает токены, только факт подтверждения).
 * При ошибке (истёк/уже использован/невалиден) — форма повторной отправки письма.
 *
 * ВАЖНО: verify ПОТРЕБЛЯЕТ токен на сервере → вызываем РОВНО один раз
 * (React 18 StrictMode запускает effect дважды; ref сохраняется в рамках маунта,
 * иначе второй вызов получит «токен уже использован»).
 */
const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState<VerifyStatus>("verifying");
  const [error, setError] = useState<string | null>(null);

  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const verifyStartedRef = useRef(false);

  useEffect(() => {
    if (verifyStartedRef.current) return;
    verifyStartedRef.current = true;

    if (!token) {
      setStatus("error");
      setError("Отсутствует токен подтверждения. Проверьте ссылку из письма.");
      return;
    }

    // Guard verifyStartedRef уже гарантирует ЕДИНСТВЕННЫЙ вызов (в т.ч. под
    // StrictMode-double-effect). Флаг «active» с cleanup НЕ используем: под StrictMode
    // cleanup обнулил бы его до резолва и результат verify был бы отброшен. setState
    // после реального unmount в React 18 — безвредный no-op.
    void (async () => {
      try {
        // httpClient бросает ApiError на не-2xx → успех = резолв без исключения.
        await authApi.verifyEmail(token);
        setStatus("success");
      } catch (err: unknown) {
        setStatus("error");
        setError(handleApiError(err, "Ссылка недействительна или истекла."));
      }
    })();
  }, [token]);

  const handleResend = async () => {
    if (!resendEmail) return;
    setResendLoading(true);
    try {
      await authApi.resendVerification(resendEmail);
    } catch {
      // Анти-enumeration: не раскрываем результат (существует ли аккаунт) — тот же
      // «тихий успех», что и на BE. Ошибку не показываем.
    } finally {
      setResendDone(true);
      setResendLoading(false);
    }
  };

  return (
    <Box sx={wrapperSx}>
      <Paper sx={cardSx}>
        <Typography variant="h4" align="center" gutterBottom>
          Подтверждение email
        </Typography>

        {status === "verifying" && (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress size={28} sx={{ mb: 2 }} />
            <Typography variant="body2">Подтверждаем ваш email…</Typography>
          </Box>
        )}

        {status === "success" && (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              Email успешно подтверждён. Теперь вы можете войти в аккаунт.
            </Alert>
            <Button
              component={RouterLink}
              to="/auth/login"
              variant="contained"
              color="primary"
              fullWidth
            >
              Войти
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error ?? "Ссылка недействительна или истекла."}
            </Alert>

            {resendDone ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                Если аккаунт с этим email существует и ещё не подтверждён —
                новое письмо отправлено. Проверьте почту.
              </Alert>
            ) : (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Введите email, чтобы отправить новую ссылку подтверждения:
                </Typography>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  disabled={resendLoading}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleResend}
                  disabled={resendLoading || !resendEmail}
                >
                  {resendLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Отправить повторно"
                  )}
                </Button>
              </Box>
            )}

            <Box sx={{ textAlign: "center" }}>
              <Link component={RouterLink} to="/auth/login" variant="body2">
                Вернуться к странице входа
              </Link>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default VerifyEmailPage;
